import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { 
  Plus, Play, Pause, Trash2, Eye, Clock, Megaphone, FlaskConical, 
  Calendar, Upload, Users, FileText, Settings, X, ChevronDown, ChevronUp,
  SplitSquareHorizontal, Edit3, RotateCcw
} from "lucide-react";
import { toast } from "sonner";
import { MessageToolbar } from "../components/MessageToolbar";

const API = '/api';

const statusBadge = (status) => {
  const map = {
    active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
    paused: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20",
    completed: "bg-blue-500/15 text-blue-400 border-blue-500/20",
    draft: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
    failed: "bg-red-500/15 text-red-400 border-red-500/20",
    scheduled: "bg-cyan-500/15 text-cyan-400 border-cyan-500/20",
  };
  const labelMap = {
    active: "Activo", paused: "Pausado", completed: "Completado",
    draft: "Borrador", failed: "Fallido", scheduled: "Programado"
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold border ${map[status] || map.draft}`}>
      {labelMap[status] || status}
    </span>
  );
};

const DAYS_OF_WEEK = [
  { key: "monday", label: "Lun" },
  { key: "tuesday", label: "Mar" },
  { key: "wednesday", label: "Mié" },
  { key: "thursday", label: "Jue" },
  { key: "friday", label: "Vie" },
  { key: "saturday", label: "Sáb" },
  { key: "sunday", label: "Dom" },
];

export default function Campaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [contactLists, setContactLists] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [contactsMode, setContactsMode] = useState("existing"); // "existing" or "new"
  const [messageMode, setMessageMode] = useState("existing"); // "existing" or "new"
  const campaignTextareaRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  
  const [form, setForm] = useState({
    name: "",
    template_ids: [],
    contact_list_id: "",
    profile_ids: [],
    scheduled_at: "",
    // New contacts inline
    new_contacts_text: "",
    new_list_name: "",
    // New message inline  
    new_message_text: "",
    new_template_name: "",
    // Timing settings (will be overwritten by global settings on load)
    delay_min: 60,
    delay_max: 180,
    messages_per_hour: 60,
    // Schedule settings
    active_days: [0, 1, 2, 3, 4],
    active_hours: [{ start: "08:00", end: "21:00" }],
    // Sending behavior
    human_typing: false,
    typing_speed: "normal",
    rcs_only: false,
  });
  
  const [globalSettings, setGlobalSettings] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [c, t, cl, p, s] = await Promise.all([
        axios.get(`${API}/campaigns`),
        axios.get(`${API}/templates`),
        axios.get(`${API}/contact-lists`),
        axios.get(`${API}/profiles`),
        axios.get(`${API}/settings`)
      ]);
      setCampaigns(c.data);
      setTemplates(t.data);
      setContactLists(cl.data);
      setProfiles(p.data);
      const settings = s.data;
      setGlobalSettings(settings);
      // Apply global settings as form defaults (only on first load)
      if (!globalSettings) {
        const dayNumToName = {0: "monday", 1: "tuesday", 2: "wednesday", 3: "thursday", 4: "friday", 5: "saturday", 6: "sunday"};
        const activeDays = (settings.active_days || [0,1,2,3,4]).map(d => typeof d === 'number' ? dayNumToName[d] : d);
        setForm(prev => ({
          ...prev,
          delay_min: settings.delay_min || 60,
          delay_max: settings.delay_max || 180,
          messages_per_hour: settings.messages_per_hour || 60,
          active_days: activeDays,
          active_hours: settings.active_hours || [{ start: "08:00", end: "21:00" }],
          human_typing: settings.human_typing || false,
          typing_speed: settings.typing_speed || "normal",
          rcs_only: settings.rcs_only || false,
        }));
      }
    } catch (e) {
      // error silenced
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const listName = form.new_list_name || `Campaña ${form.name || new Date().toLocaleDateString()}`;
      const res = await axios.post(
        `${API}/contact-lists/upload?list_name=${encodeURIComponent(listName)}`, 
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${res.data.total_contacts} contactos importados`);
      setForm(prev => ({ ...prev, contact_list_id: res.data.id }));
      setContactsMode("existing");
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail || "Error al importar archivo";
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const createContactsFromText = async () => {
    const lines = form.new_contacts_text.split(/[\r\n]+/).filter(l => l.trim());
    if (lines.length === 0) {
      toast.error("Ingresa al menos un contacto");
      return null;
    }

    const contacts = lines.map(line => {
      const parts = line.split(/[,;\t]/).map(p => p.trim());
      return {
        phone: parts[0],
        name: parts[1] || ""
      };
    }).filter(c => c.phone && c.phone.length > 5);

    if (contacts.length === 0) {
      toast.error("No se encontraron números válidos");
      return null;
    }

    try {
      const res = await axios.post(`${API}/contact-lists/manual`, {
        name: form.new_list_name || `Campaña ${form.name || new Date().toLocaleDateString()}`,
        contacts
      });
      return res.data.id;
    } catch (e) {
      toast.error("Error al crear lista de contactos");
      return null;
    }
  };

  const createTemplateFromText = async () => {
    if (!form.new_message_text.trim()) {
      toast.error("Ingresa el mensaje");
      return null;
    }

    try {
      const res = await axios.post(`${API}/templates`, {
        name: form.new_template_name || `Mensaje ${form.name || new Date().toLocaleDateString()}`,
        messages: [form.new_message_text]
      });
      return res.data.id;
    } catch (e) {
      toast.error("Error al crear plantilla");
      return null;
    }
  };

  const createCampaign = async () => {
    if (!form.name) {
      return toast.error("Ingresa un nombre para la campaña");
    }
    if (form.profile_ids.length === 0) {
      return toast.error("Selecciona al menos un perfil");
    }

    let contactListId = form.contact_list_id;
    let templateIds = form.template_ids;

    // Create contacts if needed
    if (contactsMode === "new" && !contactListId) {
      contactListId = await createContactsFromText();
      if (!contactListId) return;
    }

    if (!contactListId) {
      return toast.error("Selecciona o crea una lista de contactos");
    }

    // Create template if needed
    if (messageMode === "new" && templateIds.length === 0) {
      const newTemplateId = await createTemplateFromText();
      if (!newTemplateId) return;
      templateIds = [newTemplateId];
    }

    if (templateIds.length === 0) {
      return toast.error("Selecciona o crea un mensaje");
    }

    try {
      const payload = {
        name: form.name,
        template_ids: templateIds,
        contact_list_id: contactListId,
        profile_ids: form.profile_ids,
        scheduled_at: form.scheduled_at || null,
        settings: {
          delay_min: form.delay_min,
          delay_max: form.delay_max,
          messages_per_hour: form.messages_per_hour,
          active_days: form.active_days,
          active_hours: form.active_hours,
          human_typing: form.human_typing,
          typing_speed: form.typing_speed,
          rcs_only: form.rcs_only,
        }
      };

      if (editingCampaignId) {
        await axios.put(`${API}/campaigns/${editingCampaignId}`, payload);
        toast.success("Campaña actualizada");
        setEditingCampaignId(null);
      } else {
        const res = await axios.post(`${API}/campaigns`, payload);
        toast.success("Campaña creada correctamente");
        navigate(`/campaigns/${res.data.id}`);
      }
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(editingCampaignId ? "Error al actualizar" : "Error al crear campaña");
    }
  };

  const resetForm = () => {
    const dayNumToName = {0: "monday", 1: "tuesday", 2: "wednesday", 3: "thursday", 4: "friday", 5: "saturday", 6: "sunday"};
    const gs = globalSettings || {};
    const activeDays = (gs.active_days || [0,1,2,3,4]).map(d => typeof d === 'number' ? dayNumToName[d] : d);
    setForm({
      name: "",
      template_ids: [],
      contact_list_id: "",
      profile_ids: [],
      scheduled_at: "",
      new_contacts_text: "",
      new_list_name: "",
      new_message_text: "",
      new_template_name: "",
      delay_min: gs.delay_min || 60,
      delay_max: gs.delay_max || 180,
      messages_per_hour: gs.messages_per_hour || 60,
      active_days: activeDays,
      active_hours: gs.active_hours || [{ start: "08:00", end: "21:00" }],
      human_typing: gs.human_typing || false,
      typing_speed: gs.typing_speed || "normal",
      rcs_only: gs.rcs_only || false,
    });
    setContactsMode("existing");
    setMessageMode("existing");
    setShowAdvanced(false);
  };

  const startCampaign = async (id) => {
    try {
      await axios.post(`${API}/campaigns/${id}/start`);
      toast.success("Campaña iniciada");
      fetchData();
    } catch (e) {
      toast.error("Error al iniciar campaña");
    }
  };

  const pauseCampaign = async (id) => {
    try {
      await axios.post(`${API}/campaigns/${id}/pause`);
      toast.success("Campaña pausada");
      fetchData();
    } catch (e) {
      toast.error("Error al pausar campaña");
    }
  };

  const deleteCampaign = async (id) => {
    try {
      await axios.delete(`${API}/campaigns/${id}`);
      toast.success("Campaña eliminada");
      fetchData();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const restartCampaign = async (id) => {
    try {
      const res = await axios.post(`${API}/campaigns/${id}/restart`);
      toast.success(`Campaña reiniciada: ${res.data.messages_reset} mensajes reseteados`);
      fetchData();
    } catch (e) {
      toast.error("Error al reiniciar campaña");
    }
  };


  const editCampaign = async (campaign) => {
    setForm({
      name: campaign.name || '',
      template_id: campaign.template_id || '',
      contact_list_id: campaign.contact_list_id || '',
      profile_ids: campaign.profile_ids || [],
      contactMode: campaign.template_id ? 'existing' : 'new',
      messageMode: campaign.template_id ? 'existing' : 'new',
      new_contacts_text: '',
      new_list_name: '',
      new_message_text: campaign.message_text || '',
      new_template_name: '',
      delay_min: campaign.delay_min || 30,
      delay_max: campaign.delay_max || 60,
      messages_per_hour: campaign.messages_per_hour || 60,
      active_days: campaign.active_days || ["monday", "tuesday", "wednesday", "thursday", "friday"],
      active_hours: campaign.active_hours || [{ start: "09:00", end: "13:00" }, { start: "15:00", end: "20:00" }],
      human_typing: campaign.human_typing || false,
      typing_speed: campaign.typing_speed || 'normal',
      rcs_only: campaign.rcs_only || false,
    });
    setEditingCampaignId(campaign.id);
    setShowCreate(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleProfile = (pid) => {
    setForm(prev => ({
      ...prev,
      profile_ids: prev.profile_ids.includes(pid)
        ? prev.profile_ids.filter(p => p !== pid)
        : [...prev.profile_ids, pid]
    }));
  };

  const toggleDay = (day) => {
    setForm(prev => ({
      ...prev,
      active_days: prev.active_days.includes(day)
        ? prev.active_days.filter(d => d !== day)
        : [...prev.active_days, day]
    }));
  };

  const addTimeSlot = () => {
    setForm(prev => ({
      ...prev,
      active_hours: [...prev.active_hours, { start: "09:00", end: "18:00" }]
    }));
  };

  const removeTimeSlot = (index) => {
    setForm(prev => ({
      ...prev,
      active_hours: prev.active_hours.filter((_, i) => i !== index)
    }));
  };

  const updateTimeSlot = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      active_hours: prev.active_hours.map((slot, i) => 
        i === index ? { ...slot, [field]: value } : slot
      )
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="campaigns-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-white uppercase">Campañas</h1>
          <p className="text-sm text-zinc-500 mt-1">Crea y gestiona campañas de envío masivo RCS</p>
        </div>
        <button
          data-testid="create-campaign-btn"
          onClick={() => setShowCreate(!showCreate)}
          className="bg-blue-600 hover:bg-blue-700 text-white shadow-[0_0_15px_rgba(37,99,235,0.3)] rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors duration-200 active:scale-95"
        >
          <Plus className="w-4 h-4" /> Nueva Campaña
        </button>
      </div>

      {showCreate && (
        <div data-testid="create-campaign-form" className="bg-zinc-900 border border-white/10 rounded-lg p-5 space-y-6 animate-fade-in">
          <h3 className="font-heading text-lg font-semibold text-white uppercase flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-blue-500" />
            {editingCampaignId ? 'Editar Campaña' : 'Nueva Campaña'}
          </h3>

          {/* Nombre */}
          <div>
            <label className="text-xs font-medium text-zinc-400 block mb-1.5">Nombre de la Campaña *</label>
            <input
              data-testid="campaign-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-200"
              placeholder="Ej: Promo Enero 2026"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* SECCIÓN: Contactos */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-cyan-500" />
                <span className="text-xs font-medium text-zinc-400">Contactos *</span>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => setContactsMode("existing")}
                  className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                    contactsMode === "existing" 
                      ? "bg-cyan-600/20 text-cyan-400 border border-cyan-500/30" 
                      : "bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600"
                  }`}
                >
                  Lista Existente
                </button>
                <button
                  onClick={() => setContactsMode("new")}
                  className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                    contactsMode === "new" 
                      ? "bg-cyan-600/20 text-cyan-400 border border-cyan-500/30" 
                      : "bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600"
                  }`}
                >
                  Crear Nueva
                </button>
              </div>

              {contactsMode === "existing" ? (
                <select
                  data-testid="campaign-contacts-select"
                  value={form.contact_list_id}
                  onChange={(e) => setForm({ ...form, contact_list_id: e.target.value })}
                  className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-white outline-none"
                >
                  <option value="">Seleccionar lista...</option>
                  {contactLists.map(cl => (
                    <option key={cl.id} value={cl.id}>{cl.name} ({cl.total_contacts} contactos)</option>
                  ))}
                </select>
              ) : (
                <div className="space-y-3">
                  <input
                    value={form.new_list_name}
                    onChange={(e) => setForm({ ...form, new_list_name: e.target.value })}
                    className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none"
                    placeholder="Nombre de la lista (opcional)"
                  />
                  
                  <div className="relative">
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="contacts-file-upload"
                    />
                    <label
                      htmlFor="contacts-file-upload"
                      className={`flex items-center justify-center gap-2 w-full px-3 py-3 border-2 border-dashed rounded-md cursor-pointer transition-colors ${
                        uploading ? "border-blue-500 bg-blue-500/10" : "border-zinc-700 hover:border-zinc-600"
                      }`}
                    >
                      <Upload className="w-4 h-4 text-zinc-500" />
                      <span className="text-xs text-zinc-400">
                        {uploading ? "Subiendo..." : "Subir CSV o Excel"}
                      </span>
                    </label>
                  </div>

                  <div className="text-center text-xs text-zinc-600">— o —</div>

                  <textarea
                    value={form.new_contacts_text}
                    onChange={(e) => setForm({ ...form, new_contacts_text: e.target.value })}
                    className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none resize-none"
                    rows={4}
                    placeholder={"Un contacto por línea:\n+5491123456789, Juan\n+5491198765432, María"}
                  />
                  <p className="text-[10px] text-zinc-600">Formato: número, nombre (nombre es opcional)</p>
                </div>
              )}
            </div>

            {/* SECCIÓN: Mensaje */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-500" />
                <span className="text-xs font-medium text-zinc-400">Mensaje *</span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setMessageMode("existing")}
                  className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                    messageMode === "existing" 
                      ? "bg-purple-600/20 text-purple-400 border border-purple-500/30" 
                      : "bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600"
                  }`}
                >
                  Plantilla Existente
                </button>
                <button
                  onClick={() => setMessageMode("new")}
                  className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                    messageMode === "new" 
                      ? "bg-purple-600/20 text-purple-400 border border-purple-500/30" 
                      : "bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600"
                  }`}
                >
                  Escribir Nuevo
                </button>
              </div>

              {messageMode === "existing" ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {templates.map(t => (
                    <button
                      key={t.id}
                      onClick={() => {
                        setForm(prev => ({
                          ...prev,
                          template_ids: prev.template_ids.includes(t.id)
                            ? prev.template_ids.filter(x => x !== t.id)
                            : [...prev.template_ids, t.id]
                        }));
                      }}
                      className={`w-full text-left px-3 py-2 rounded-md text-xs border transition-colors flex items-center justify-between ${
                        form.template_ids.includes(t.id)
                          ? 'bg-purple-600/15 text-purple-300 border-purple-500/30'
                          : 'bg-zinc-950/50 text-zinc-400 border-zinc-800 hover:border-zinc-600'
                      }`}
                    >
                      <span>{t.name}</span>
                      {form.template_ids.includes(t.id) && form.template_ids.length > 1 && (
                        <span className="font-mono font-bold text-purple-400">
                          {String.fromCharCode(65 + form.template_ids.indexOf(t.id))}
                        </span>
                      )}
                    </button>
                  ))}
                  {templates.length === 0 && <p className="text-xs text-zinc-600 text-center py-4">No hay plantillas creadas</p>}
                  {form.template_ids.length > 1 && (
                    <div className="flex items-center gap-2 mt-1 bg-purple-500/10 rounded-md px-3 py-2">
                      <FlaskConical className="w-3.5 h-3.5 text-purple-400" />
                      <span className="text-[10px] text-purple-300">
                        A/B Test: los contactos se repartirán entre {form.template_ids.length} variantes
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <input
                    value={form.new_template_name}
                    onChange={(e) => setForm({ ...form, new_template_name: e.target.value })}
                    className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none"
                    placeholder="Nombre del mensaje (opcional)"
                  />
                  <MessageToolbar
                    textareaRef={campaignTextareaRef}
                    value={form.new_message_text || ''}
                    onChange={(val) => setForm({ ...form, new_message_text: val })}
                  />
                  <textarea
                    ref={campaignTextareaRef}
                    value={form.new_message_text}
                    onChange={(e) => setForm({ ...form, new_message_text: e.target.value })}
                    className="w-full bg-zinc-950/50 border border-zinc-800 border-t-0 focus:border-blue-500 rounded-b-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none resize-none"
                    rows={5}
                    placeholder={"Escribe tu mensaje aquí...\n\nUsa los botones de arriba para insertar variables"}
                  />
                  <p className="text-[10px] text-zinc-600">
                    Click derecho en variable para limite de chars. Ej: {"{{nombreL20}}"} = max 20
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* SECCIÓN: Perfiles */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-zinc-400">Perfiles de Envío * (solo conectados)</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {profiles.filter(p => p.status === 'connected').map(p => (
                <button
                  key={p.id}
                  onClick={() => toggleProfile(p.id)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                    form.profile_ids.includes(p.id)
                      ? 'bg-blue-600/20 text-blue-400 border-blue-500/30'
                      : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5" />
                  {p.name}
                </button>
              ))}
              {profiles.filter(p => p.status === 'connected').length === 0 && (
                <p className="text-xs text-amber-400/80">No hay perfiles conectados. Conecta un perfil desde la extension primero.</p>
              )}
            </div>
            {profiles.filter(p => p.status !== 'connected').length > 0 && (
              <p className="text-[10px] text-zinc-600 mt-1.5">
                {profiles.filter(p => p.status !== 'connected').length} perfil(es) desconectado(s) no se muestran
              </p>
            )}
            {form.profile_ids.length > 1 && (
              <p className="text-[10px] text-zinc-500 mt-2">
                Los mensajes se distribuirán entre {form.profile_ids.length} perfiles
              </p>
            )}
          </div>

          {/* SECCIÓN: Configuración Avanzada */}
          <div className="border-t border-zinc-800 pt-4">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <Settings className="w-4 h-4" />
              <span className="text-xs font-medium">Configuración Avanzada</span>
              {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showAdvanced && (
              <div className="mt-4 space-y-5 animate-fade-in">
                {/* Tiempos */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-[10px] font-medium text-zinc-500 block mb-1">Delay Mínimo (seg)</label>
                    <input
                      type="number"
                      value={form.delay_min}
                      onChange={(e) => setForm({ ...form, delay_min: parseInt(e.target.value) || 30 })}
                      className="w-full bg-zinc-950/50 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white outline-none"
                      min={5}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-zinc-500 block mb-1">Delay Máximo (seg)</label>
                    <input
                      type="number"
                      value={form.delay_max}
                      onChange={(e) => setForm({ ...form, delay_max: parseInt(e.target.value) || 60 })}
                      className="w-full bg-zinc-950/50 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white outline-none"
                      min={10}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-zinc-500 block mb-1">Mensajes/Hora (máx)</label>
                    <input
                      type="number"
                      value={form.messages_per_hour}
                      onChange={(e) => setForm({ ...form, messages_per_hour: parseInt(e.target.value) || 60 })}
                      className="w-full bg-zinc-950/50 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white outline-none"
                      min={1}
                      max={120}
                    />
                  </div>
                </div>

                {/* Días activos */}
                <div>
                  <label className="text-[10px] font-medium text-zinc-500 block mb-2">Días de Envío</label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map(day => (
                      <button
                        key={day.key}
                        onClick={() => toggleDay(day.key)}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                          form.active_days.includes(day.key)
                            ? 'bg-emerald-600/20 text-emerald-400 border-emerald-500/30'
                            : 'bg-zinc-800 text-zinc-500 border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Horarios */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[10px] font-medium text-zinc-500">Horarios de Envío</label>
                    <button
                      onClick={addTimeSlot}
                      className="text-xs text-blue-400 hover:text-blue-300"
                    >
                      + Agregar horario
                    </button>
                  </div>
                  <div className="space-y-2">
                    {form.active_hours.map((slot, i) => (
                      <div key={`time-slot-${i}`} className="flex items-center gap-2">
                        <input
                          type="time"
                          value={slot.start}
                          onChange={(e) => updateTimeSlot(i, "start", e.target.value)}
                          className="bg-zinc-950/50 border border-zinc-800 rounded-md px-3 py-1.5 text-sm text-white outline-none [color-scheme:dark]"
                        />
                        <span className="text-zinc-500">a</span>
                        <input
                          type="time"
                          value={slot.end}
                          onChange={(e) => updateTimeSlot(i, "end", e.target.value)}
                          className="bg-zinc-950/50 border border-zinc-800 rounded-md px-3 py-1.5 text-sm text-white outline-none [color-scheme:dark]"
                        />
                        {form.active_hours.length > 1 && (
                          <button
                            onClick={() => removeTimeSlot(i)}
                            className="p-1 text-red-400 hover:text-red-300"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Programar inicio */}
                <div>
                  <label className="text-[10px] font-medium text-zinc-500 flex items-center gap-1 mb-1">
                    <Calendar className="w-3 h-3" />
                    Programar Inicio (opcional)
                  </label>
                  <input
                    type="datetime-local"
                    value={form.scheduled_at ? form.scheduled_at.slice(0, 16) : ""}
                    onChange={(e) => setForm({ ...form, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                    className="w-full bg-zinc-950/50 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white outline-none [color-scheme:dark]"
                  />
                  <p className="text-[10px] text-zinc-600 mt-1">
                    {form.scheduled_at
                      ? `Inicio automático: ${new Date(form.scheduled_at).toLocaleString('es-ES')}`
                      : "Dejar vacío para iniciar manualmente"
                    }
                  </p>
                </div>

                {/* Sending behavior */}
                <div className="border-t border-zinc-800 pt-3 space-y-3">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Comportamiento de envío</p>

                  {/* Human Typing */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-zinc-300">Escritura humana</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" checked={form.human_typing}
                        onChange={e => setForm({...form, human_typing: e.target.checked})} />
                      <div className="w-9 h-5 bg-zinc-700 peer-checked:bg-purple-600 rounded-full transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
                    </label>
                  </div>
                  {form.human_typing && (
                    <div className="flex items-center justify-between pl-4">
                      <span className="text-xs text-zinc-400">Velocidad</span>
                      <select value={form.typing_speed} onChange={e => setForm({...form, typing_speed: e.target.value})}
                        className="bg-zinc-800 border border-zinc-700 rounded-md px-2 py-1 text-xs text-white outline-none">
                        <option value="slow">Lenta (50-150ms)</option>
                        <option value="normal">Normal (20-70ms)</option>
                        <option value="fast">Rápida (5-25ms)</option>
                      </select>
                    </div>
                  )}

                  {/* RCS Only */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-zinc-300">Solo enviar a RCS</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" checked={form.rcs_only}
                        onChange={e => setForm({...form, rcs_only: e.target.checked})} />
                      <div className="w-9 h-5 bg-zinc-700 peer-checked:bg-green-600 rounded-full transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Botones */}
          <div className="flex gap-2 pt-2 border-t border-zinc-800">
            <button
              data-testid="save-campaign-btn"
              onClick={createCampaign}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 text-sm font-medium transition-colors active:scale-95"
            >
              {editingCampaignId ? 'Guardar Cambios' : 'Crear Campaña'}
            </button>
            <button
              onClick={() => { setShowCreate(false); setEditingCampaignId(null); resetForm(); }}
              className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-md px-4 py-2 text-sm transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista de campañas */}
      <div className="bg-zinc-900 border border-white/5 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-zinc-900/50 text-xs uppercase tracking-wider text-zinc-500 font-bold border-b border-white/5">
              <th className="py-3 px-4 text-left">Campaña</th>
              <th className="py-3 px-4 text-left">Estado</th>
              <th className="py-3 px-4 text-center">Tipo</th>
              <th className="py-3 px-4 text-center">Progreso</th>
              <th className="py-3 px-4 text-center">Perfiles</th>
              <th className="py-3 px-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c, i) => {
              const processed = (c.stats?.sent || 0) + (c.stats?.failed || 0) + (c.stats?.skipped_no_rcs || 0) + (c.stats?.skipped || 0) + (c.stats?.error || 0) + (c.stats?.blocked_sms || 0);
              const progress = c.status === 'completed' ? 100 : (c.stats?.total > 0 ? Math.round((processed / c.stats.total) * 100) : 0);
              return (
                <tr key={c.id} data-testid={`campaign-row-${i}`} className="hover:bg-white/[0.02] transition-colors border-b border-white/5 last:border-0">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-zinc-200 font-medium">{c.name}</p>
                      {c.is_ab_test && (
                        <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-semibold bg-purple-500/15 text-purple-400">
                          <FlaskConical className="w-2.5 h-2.5" /> A/B
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] font-mono text-zinc-600 mt-0.5">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {new Date(c.created_at).toLocaleDateString('es-ES')}
                      {c.scheduled_at && c.status === 'scheduled' && (
                        <span className="ml-2 text-cyan-500">
                          <Calendar className="w-3 h-3 inline mr-0.5" />
                          {new Date(c.scheduled_at).toLocaleString('es-ES', {dateStyle: 'short', timeStyle: 'short'})}
                        </span>
                      )}
                    </p>
                  </td>
                  <td className="py-3 px-4">{statusBadge(c.status)}</td>
                  <td className="py-3 px-4 text-center">
                    {c.settings?.rcs_only ? (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                        Solo RCS
                      </span>
                    ) : c.settings?.prefer_rcs === false ? (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/20">
                        SMS
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-sky-500/15 text-sky-400 border border-sky-500/20">
                        RCS + SMS
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center gap-2 justify-center">
                      <div className="w-20 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                      </div>
                      <span className="text-xs font-mono text-zinc-400">{progress}%</span>
                    </div>
                    <span className="text-[10px] text-zinc-600">
                      {processed}/{c.stats?.total || 0}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="text-xs font-mono text-zinc-400">{c.profile_ids?.length || 0}</span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => navigate(`/campaigns/${c.id}`)}
                        className="p-1.5 hover:bg-zinc-800 rounded transition-colors text-zinc-400 hover:text-white"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {(c.status === 'draft' || c.status === 'paused') && (
                        <button
                          onClick={() => startCampaign(c.id)}
                          className="p-1.5 hover:bg-emerald-500/10 rounded transition-colors text-emerald-400"
                          title="Iniciar"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                      )}
                      {c.status === 'draft' && (
                        <button
                          onClick={() => editCampaign(c)}
                          className="p-1.5 hover:bg-blue-500/10 rounded transition-colors text-blue-400"
                          title="Editar"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                      )}
                      {c.status === 'active' && (
                        <button
                          onClick={() => pauseCampaign(c.id)}
                          className="p-1.5 hover:bg-yellow-500/10 rounded transition-colors text-yellow-400"
                          title="Pausar"
                        >
                          <Pause className="w-4 h-4" />
                        </button>
                      )}
                      {(c.status === 'completed' || c.status === 'paused') && (
                        <button
                          data-testid={`restart-campaign-${c.id}`}
                          onClick={() => restartCampaign(c.id)}
                          className="p-1.5 hover:bg-orange-500/10 rounded transition-colors text-orange-400"
                          title="Reiniciar (resetear mensajes fallidos)"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteCampaign(c.id)}
                        className="p-1.5 hover:bg-red-500/10 rounded transition-colors text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {campaigns.length === 0 && (
          <div className="text-center py-12">
            <Megaphone className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">No hay campañas creadas</p>
          </div>
        )}
      </div>
    </div>
  );
}
